import { IProvider } from '@/shared/IProvider';
import { ApiService } from './ApiService';
import { StorageCacheService } from './StorageCacheService';
import { IUserInfo } from '@/shared/IUserInfo';
import { UserInfoService } from './UserInfoService';
import { INVITE_TYPE } from '@/shared/constants';
import { trimString } from '@/shared/parse';
import { TelemetryClientService } from './TelemetryClientService';

export class CourseProviderStorage {

  static async getProvider(id: string, force?: boolean): Promise<IProvider | undefined> {
    const cached = StorageCacheService.getValue<IProvider>(`provider/${id}`);
    if (!force && cached) {
      return cached;
    }

    const result = await ApiService.fetch<IProvider>(`/api/provider/${id}`);
    if (result) {
      StorageCacheService.setValue(`provider/${id}`, result);
      return result;
    }
  }

  private static getPrimaryContact(provider: IProvider): IUserInfo {
    return {
      name: provider.ProviderContact,
      email: provider.ProviderEmail,
      id: '',
    };
  }

  static async createProvider(provider: IProvider, invite: boolean): Promise<IProvider> {

    const primaryContact = this.getPrimaryContact(provider);
    const { ensured, invited } = await UserInfoService.ensureUser(primaryContact, invite ? INVITE_TYPE.PROVIDER : INVITE_TYPE.NONE);

    const result = await ApiService.fetch<IProvider>(`/api/provider`, 'POST', <IProvider>{
      ...provider,
      ProviderName: trimString(provider.ProviderName),
      ProviderIdentifier: trimString(provider.ProviderIdentifier),
      ProviderAddress: trimString(provider.ProviderAddress),
      ProviderContact: trimString(provider.ProviderContact),
      ProviderPhone: trimString(provider.ProviderPhone),
      ProviderWebsite: trimString(provider.ProviderWebsite),
      ProviderEmail: ensured.email,
    });

    if (invite && !invited) {
      await CourseProviderStorage.inviteProvider(result.id, ensured.email);
    }

    StorageCacheService.resetCache(`provider`);
    StorageCacheService.setValue(`provider/${result.id}`, result);
    return result;
  }

  static async inviteProvider(id: string, email?: string, name?: string): Promise<IProvider> {
    const result = await ApiService.fetch<IProvider>(`/api/provider/${id}/invite`, 'POST', { email, name });
    return result;
  }

  static async patchProvider(provider: IProvider, invite: boolean): Promise<IProvider> {

    const primaryContact = this.getPrimaryContact(provider);
    const { ensured, invited } = await UserInfoService.ensureUser(primaryContact, invite ? INVITE_TYPE.PROVIDER : INVITE_TYPE.NONE);

    const result = await ApiService.fetch<IProvider>(`/api/provider/${provider.id}`, 'PATCH', provider);

    if (invite && !invited) {
      await CourseProviderStorage.inviteProvider(result.id, ensured.email);
    }

    StorageCacheService.resetCache(`provider`);
    StorageCacheService.setValue(`provider/${provider.id}`, result);
    return result;
  }

  static async deleteProvider(id: string): Promise<IProvider> {
    const result = await ApiService.fetch<IProvider>(`/api/provider/${id}`, 'DELETE');
    StorageCacheService.resetCache(`provider`);
    return result;
  }

  static async getProviders(force?: boolean): Promise<IProvider[]> {
    if (!force) {
      const result = StorageCacheService.getValue<IProvider[]>('provider');
      if (result) {
        return result;
      }
    }

    const items = await ApiService.fetch<IProvider[]>(`/api/provider`);

    StorageCacheService.resetCache(`provider`);
    StorageCacheService.setValue(`provider`, items);
    for (const item of items) {
      StorageCacheService.setValue(`provider/${item.id}`, item);
    }

    return items;
  }

  static async getFieldChoices(fieldName: string): Promise<string[]> {
    try {
      return await ApiService.fetch<string[]>(`/api/provider/field/${fieldName}/choices`);
    } catch (error) {
      TelemetryClientService.error(`Failed to load choices for field ${fieldName} from server.`, error);
      return [];
    }
  }

}